import { Spacing } from '@persona-id/design-tokens';
import { ChangeEvent, FC } from 'react';
import { Control, Controller } from 'react-hook-form';
import { EventOrValueHandler } from 'redux-form';
import RangeInput from 'shared/components/RangeInput';
import { COLORS } from 'shared/styles/variables';
import styled from 'styled-components';

import { LinkedStyleInputs } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/inputs';
import ColorInput from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/inputs/ColorInput';
import ReactSelect from 'dashboard/components/ReactSelect';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import { ThemeSetAttributes } from 'dashboard/models/theme-set';
import {
  ThemeSetEditorFormControlConfig,
  ThemeSetEditorFormControlType,
} from 'dashboard/views/DashboardThemeSetsEditorNewView/theme-set-editor-form-categories';
import {
  getUnitFromObject,
  getValueFromObject,
} from 'dashboard/views/DashboardThemeSetsEditorNewView/util';
import WCAGWarning from 'dashboard/views/DashboardThemeSetsEditorNewView/WCAGWarning';
import Select, { Option } from 'design-system/components/Select';
import Stack from 'design-system/components/Stack/Stack';
import { Label } from 'design-system/components/Text';
import { StyleValueBoxDimensionsConfig } from 'lib/inquiryStyles';
import { BrowserFonts, GoogleFonts } from 'lib/interfaces/fonts';
import { assertNever } from 'lib/utils/types';

const ALL_FONT_OPTIONS = Object.values({ ...BrowserFonts, ...GoogleFonts })
  .sort((a, b) => a.localeCompare(b))
  .map((fontValue) => ({
    label: <div style={{ fontFamily: fontValue }}>{fontValue}</div>,
    value: fontValue,
  }));

const Hint = styled('p')`
  color: ${COLORS.gray50};
  margin-top: -${Spacing[1]};
`;

interface ThemeSetEditorFormControlProps {
  controlConfig: ThemeSetEditorFormControlConfig;
  control: Control<ThemeSetAttributes, object>;
  disabled?: boolean;
}

/**
 * Dynamically renders a form control for the theme set editor based on the given type.
 */
const ThemeSetEditorFormControl: FC<ThemeSetEditorFormControlProps> = ({
  controlConfig,
  control,
  disabled,
}) => {
  const { label, description, name, controlType } = controlConfig;
  const id = `theme-set-editor__${name}`;

  return (
    <FormGroup>
      <Stack>
        <Label htmlFor={id} size='md' weight='bold'>
          {label}
        </Label>
        {description && <Hint>{description}</Hint>}
        <Controller
          name={name}
          control={control}
          render={({ field: { value, onChange } }) => {
            switch (controlType.type) {
              case ThemeSetEditorFormControlType.BoxDimensions: {
                const boxDimensions: StyleValueBoxDimensionsConfig =
                  value && typeof value === 'object'
                    ? {
                        top: 'top' in value ? value.top : null,
                        bottom: 'bottom' in value ? value.bottom : null,
                        left: 'left' in value ? value.left : null,
                        right: 'right' in value ? value.right : null,
                      }
                    : {
                        top: null,
                        bottom: null,
                        left: null,
                        right: null,
                      };
                return (
                  <LinkedStyleInputs
                    id={id}
                    defaultValues={boxDimensions}
                    rootStyle=''
                    submitSingle={(value: string, field: string) => {
                      const updatedSize = { value: parseInt(value), unit: 'px' };
                      const updatedBoxDimensions = {
                        ...boxDimensions,
                        [field]: updatedSize,
                      };
                      onChange(updatedBoxDimensions);
                    }}
                    submitLinked={(value: string) => {
                      const updatedSize = { value: parseInt(value), unit: 'px' };
                      const updatedBoxDimensions = {
                        top: updatedSize,
                        bottom: updatedSize,
                        left: updatedSize,
                        right: updatedSize,
                      };
                      onChange(updatedBoxDimensions);
                    }}
                    editable={!disabled}
                  />
                );
              }
              case ThemeSetEditorFormControlType.Color:
                return (
                  <div>
                    <ColorInput
                      id={id}
                      name={name}
                      value={getValueFromObject(value)}
                      onChange={
                        ((hexValue: string) => {
                          if (hexValue) {
                            onChange({ value: hexValue, unit: 'hex' });
                          } else {
                            onChange(null);
                          }
                        }) as EventOrValueHandler<ChangeEvent<string>>
                      }
                      disabled={disabled}
                      // NOTE (Fawn): `warning` is intentionally left empty here, and is instead handled by the WCAGWarning component
                      warning=''
                    />
                    <WCAGWarning
                      hexColor={getValueFromObject(value)}
                      checkComplianceAgainst={controlType.checkWCAGComplianceAgainst}
                      control={control}
                    />
                  </div>
                );
              case ThemeSetEditorFormControlType.FontFamily: {
                return (
                  <ReactSelect
                    id={id}
                    aria-label={label}
                    options={ALL_FONT_OPTIONS}
                    value={value}
                    onChange={(value) => {
                      onChange(value);
                    }}
                    disabled={disabled}
                  />
                );
              }
              case ThemeSetEditorFormControlType.Select:
                return (
                  <Select
                    id={id}
                    value={typeof value === 'string' ? value : null}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={disabled}
                    placeholder='Select...'
                  >
                    {controlType.options.map(({ label, value }) => (
                      <Option key={value} value={value}>
                        {label}
                      </Option>
                    ))}
                  </Select>
                );
              case ThemeSetEditorFormControlType.Size:
                return (
                  <RangeInput
                    id={id}
                    min={controlType.min}
                    max={controlType.max}
                    value={getValueFromObject(value)}
                    unit={getUnitFromObject(value)}
                    onChange={(newValue: number) => {
                      onChange({ value: newValue, unit: 'px' });
                    }}
                    disabled={disabled}
                  />
                );
              default:
                assertNever(controlType);
                return <></>;
            }
          }}
        />
      </Stack>
    </FormGroup>
  );
};

export default ThemeSetEditorFormControl;
